import { useEffect } from 'react';

import { usePageContext } from './usePageContext.js';
import { PagePanelsActions } from '../types/page-state.js';
import type { DismissiblePagePanels } from '../types/shared-page-types.js';

type UseConditionalPagePanelProps = {
  /** The name of the panel. */
  name: DismissiblePagePanels;
  /** The default dismissed state of the panel (uncontrolled). */
  defaultDismissed?: boolean;
  /** The dismissed state of the panel (controlled). */
  dismissed?: boolean;
  /** Whether the panel should be shown depending on the current viewport size. */
  showPanel: boolean;
  /** The breakpoint where the panel switches from a drawer to a panel. */
  breakpoint: number;
};

/**
 * The hook is used to set the initial state of the side panels and to update
 * the panel's state depending on the received props.
 * @internal
 */
export function useConditionalPagePanel(
  props: UseConditionalPagePanelProps,
): void {
  const { name, defaultDismissed, dismissed, showPanel, breakpoint } = props;
  const { dispatch, setPanelDismissed } = usePageContext();

  // Set the initial state of the panel on mount according to the passed props.
  useEffect(() => {
    dispatch({
      type: PagePanelsActions.SETPANEL,
      payload: {
        name,
        defaultDismissed: defaultDismissed ?? false,
        drawer: !showPanel,
        controlled: dismissed !== undefined,
        breakpoint,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update the drawer state due to hitting a breakpoint.
  // The page then handles which panels are currently open.
  useEffect(() => {
    dispatch({
      type: PagePanelsActions.SETPANEL,
      payload: { name, drawer: !showPanel },
    });

    // Excluding dependencies:
    //    - defaultDismissed: only used to initialize states
    //    - name: panel name will never change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPanel]);

  // Panel state update due to controlled prop changing.
  useEffect(() => {
    if (defaultDismissed === undefined && dismissed !== undefined) {
      setPanelDismissed(name, dismissed);
    }
    // Excluding dependencies:
    //    - defaultDismissed: only used to initialize states
    //    - name: panel name will never change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed, dispatch]);
}
